import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Lightbulb, Plus, Trash2, Trophy, XCircle, Filter, BookOpen, Brain, Loader2, TrendingUp, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const outcomeStyles = {
  castigat: { label: 'Castigat', class: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20', icon: Trophy },
  pierdut: { label: 'Pierdut', class: 'bg-rose-500/10 text-rose-400 ring-rose-500/20', icon: XCircle },
  anulat: { label: 'Anulat', class: 'bg-zinc-500/10 text-zinc-400 ring-zinc-500/20', icon: XCircle },
};

export default function LessonsLearnedPage() {
  const [lessons, setLessons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [applyLesson, setApplyLesson] = useState(null);
  const [applyProject, setApplyProject] = useState('');
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({
    project_id: '', project_name: '', outcome: 'pierdut',
    what_went_well: '', what_went_wrong: '', lessons: '', tags: [], final_score: 0
  });

  const load = () => {
    Promise.all([
      API.get('/lessons', { params: { outcome: filter !== 'all' ? filter : undefined } }),
      API.get('/projects')
    ]).then(([l, p]) => { setLessons(l.data); setProjects(p.data); })
    .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const handleSave = async () => {
    if (!form.project_name && !form.project_id) { toast.error('Selecteaza un proiect'); return; }
    try {
      await API.post('/lessons', form);
      toast.success('Lectie salvata!');
      setForm({ project_id: '', project_name: '', outcome: 'pierdut', what_went_well: '', what_went_wrong: '', lessons: '', tags: [], final_score: 0 });
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Stergi aceasta lectie?')) return;
    try { await API.delete(`/lessons/${id}`); toast.success('Stearsa'); load(); } catch { toast.error('Eroare'); }
  };

  const handleInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await API.post('/lessons/insights');
      setInsights(res.data);
      toast.success('AI Insights generate!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la generare insights'); }
    finally { setInsightsLoading(false); }
  };

  const selectProject = (pid) => {
    const p = projects.find(pr => pr.id === pid);
    if (p) setForm({ ...form, project_id: pid, project_name: p.name });
  };

  const handleApplyToMatrix = async () => {
    if (!applyProject) { toast.error('Selectează proiectul țintă'); return; }
    setApplying(true);
    try {
      const res = await API.post(`/lessons/${applyLesson.id}/to-compliance`, { project_id: applyProject });
      toast.success(`${res.data.added} cerințe preventive adăugate în Matricea de Conformitate — ${res.data.project_name}`);
      setApplyLesson(null); setApplyProject('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la generarea cerințelor'); }
    finally { setApplying(false); }
  };

  const wonCount = lessons.filter(l => l.outcome === 'castigat').length;
  const lostCount = lessons.filter(l => l.outcome === 'pierdut').length;
  const winRate = lessons.length > 0 ? Math.round((wonCount / lessons.length) * 100) : 0;

  return (
    <div className="p-8" data-testid="lessons-learned-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <Lightbulb className="w-6 h-6 text-amber-400" strokeWidth={1.5} />Lecții Învățate
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Baza de cunostinte din licitatii finalizate</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lesson-btn"><Plus className="w-4 h-4 mr-2" />Adauga Lectie</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Lectie Noua</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Proiect</Label>
                <Select value={form.project_id} onValueChange={selectProject}>
                  <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10"><SelectValue placeholder="Selecteaza proiect" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rezultat</Label>
                <Select value={form.outcome} onValueChange={v => setForm({...form, outcome: v})}>
                  <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="castigat">Castigat</SelectItem>
                    <SelectItem value="pierdut">Pierdut</SelectItem>
                    <SelectItem value="anulat">Anulat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scor final (%)</Label>
                <Input type="number" value={form.final_score} onChange={e => setForm({...form, final_score: parseInt(e.target.value)||0})}
                  className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" />
              </div>
              <div>
                <Label>Ce a mers bine</Label>
                <Textarea value={form.what_went_well} onChange={e => setForm({...form, what_went_well: e.target.value})}
                  placeholder="Punctele forte ale ofertei..." className="mt-1.5 bg-zinc-900/50 border-white/10 min-h-[80px]" />
              </div>
              <div>
                <Label>Ce nu a mers bine</Label>
                <Textarea value={form.what_went_wrong} onChange={e => setForm({...form, what_went_wrong: e.target.value})}
                  placeholder="Punctele slabe, probleme intampinate..." className="mt-1.5 bg-zinc-900/50 border-white/10 min-h-[80px]" />
              </div>
              <div>
                <Label>Lectii invatate</Label>
                <Textarea value={form.lessons} onChange={e => setForm({...form, lessons: e.target.value})}
                  placeholder="Ce am invatat pentru viitor..." className="mt-1.5 bg-zinc-900/50 border-white/10 min-h-[80px]" />
              </div>
              <Button onClick={handleSave} className="w-full" data-testid="save-lesson-btn">Salveaza Lectie</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-card border-white/10"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold font-mono-data">{lessons.length}</p><p className="text-[10px] text-muted-foreground uppercase">Total Lectii</p>
        </CardContent></Card>
        <Card className="bg-card border-white/10"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400 font-mono-data">{wonCount}</p><p className="text-[10px] text-muted-foreground uppercase">Castigate</p>
        </CardContent></Card>
        <Card className="bg-card border-white/10"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-rose-400 font-mono-data">{lostCount}</p><p className="text-[10px] text-muted-foreground uppercase">Pierdute</p>
        </CardContent></Card>
        <Card className="bg-card border-white/10"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-400 font-mono-data">{winRate}%</p><p className="text-[10px] text-muted-foreground uppercase">Rata Castig</p>
        </CardContent></Card>
      </div>

      {/* AI Insights Section */}
      <Card className="bg-card border-white/10 mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>AI Pattern Analysis</h3>
              <span className="text-[10px] text-muted-foreground">Analiza automata a pattern-urilor din lectiile invatate</span>
            </div>
            <Button onClick={handleInsights} disabled={insightsLoading || lessons.length < 1} size="sm" variant="outline" data-testid="generate-insights-btn">
              {insightsLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
              {insightsLoading ? 'Se analizeaza...' : 'Genereaza Insights'}
            </Button>
          </div>
          {insights ? (
            <div className="space-y-3">
              {insights.pattern_summary && <p className="text-sm text-foreground bg-zinc-900/50 p-3 rounded-lg border border-white/5">{insights.pattern_summary}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {insights.win_patterns?.length > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                    <p className="text-[10px] uppercase text-emerald-400 font-semibold mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Pattern-uri Castig</p>
                    {insights.win_patterns.map((p, i) => <p key={i} className="text-xs text-muted-foreground mb-1">- {p}</p>)}
                  </div>
                )}
                {insights.loss_patterns?.length > 0 && (
                  <div className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/10">
                    <p className="text-[10px] uppercase text-rose-400 font-semibold mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Pattern-uri Pierdere</p>
                    {insights.loss_patterns.map((p, i) => <p key={i} className="text-xs text-muted-foreground mb-1">- {p}</p>)}
                  </div>
                )}
              </div>
              {insights.top_recommendations?.length > 0 && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-[10px] uppercase text-primary font-semibold mb-2">Recomandari Prioritare</p>
                  {insights.top_recommendations.map((r, i) => <p key={i} className="text-xs text-muted-foreground mb-1">{i+1}. {r}</p>)}
                </div>
              )}
            </div>
          ) : <p className="text-xs text-muted-foreground text-center">Apasa "Genereaza Insights" pentru a analiza pattern-urile</p>}
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-zinc-900/50 border-white/10" data-testid="lessons-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="castigat">Castigate</SelectItem>
            <SelectItem value="pierdut">Pierdute</SelectItem>
            <SelectItem value="anulat">Anulate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lessons List */}
      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-white/5 animate-pulse rounded-lg" />)}</div>
      ) : lessons.length === 0 ? (
        <Card className="bg-card border-white/10 border-dashed">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground mb-2">Nicio lectie invatata</p>
            <Button onClick={() => setOpen(true)} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Adauga prima lectie</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {lessons.map(lesson => {
            const oc = outcomeStyles[lesson.outcome] || outcomeStyles.pierdut;
            const OcIcon = oc.icon;
            return (
              <Card key={lesson.id} className="bg-card border-white/10 hover:border-white/20 transition-all" data-testid={`lesson-${lesson.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={`${oc.class} ring-1 ring-inset border-0 text-xs`}>
                        <OcIcon className="w-3 h-3 mr-1" />{oc.label}
                      </Badge>
                      <span className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>{lesson.project_name}</span>
                      {lesson.final_score > 0 && <span className="text-xs text-muted-foreground font-mono-data">{lesson.final_score}%</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{new Date(lesson.created_at).toLocaleDateString('ro-RO')}</span>
                      <button onClick={() => { setApplyLesson(lesson); setApplyProject(''); }} title="Transformă în cerințe pentru Matricea de Conformitate" className="p-1 rounded hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-400" data-testid={`lesson-to-matrix-${lesson.id}`}><ShieldCheck className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(lesson.id)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {lesson.what_went_well && (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                        <p className="text-[10px] uppercase text-emerald-400 font-semibold mb-1">Ce a mers bine</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{lesson.what_went_well}</p>
                      </div>
                    )}
                    {lesson.what_went_wrong && (
                      <div className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/10">
                        <p className="text-[10px] uppercase text-rose-400 font-semibold mb-1">Ce nu a mers bine</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{lesson.what_went_wrong}</p>
                      </div>
                    )}
                    {lesson.lessons && (
                      <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                        <p className="text-[10px] uppercase text-amber-400 font-semibold mb-1">Lectii invatate</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{lesson.lessons}</p>
                      </div>
                    )}
                  </div>
                  {lesson.tags?.length > 0 && (
                    <div className="flex gap-1 mt-3">{lesson.tags.map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{t}</span>)}</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!applyLesson} onOpenChange={o => { if (!o) { setApplyLesson(null); setApplyProject(''); } }}>
        <DialogContent data-testid="apply-matrix-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Transformă lecția în cerințe de conformitate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">AI transformă cauzele din lecția „<span className="text-foreground font-medium">{applyLesson?.project_name}</span>" în cerințe preventive verificabile și le adaugă în Matricea de Conformitate a proiectului ales, ca aceeași greșeală să nu se repete.</p>
            <div>
              <Label>Proiect țintă</Label>
              <Select value={applyProject} onValueChange={setApplyProject}>
                <SelectTrigger className="mt-1.5 bg-zinc-900/50 border-white/10" data-testid="apply-matrix-project"><SelectValue placeholder="Selectează proiectul" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyToMatrix} disabled={applying || !applyProject} className="w-full" data-testid="apply-matrix-submit">
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se generează cerințele…</> : <><ShieldCheck className="w-4 h-4 mr-2" />Adaugă în Matricea de Conformitate</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
